import bcrypt from 'bcryptjs';
import CryptoJS from 'crypto-js';

// Configuration
const SALT_ROUNDS = 12; // Higher rounds = more secure but slower
const INVITATION_TOKEN_SECRET = process.env.REACT_APP_INVITATION_SECRET;
if (!INVITATION_TOKEN_SECRET) {
  console.error('REACT_APP_INVITATION_SECRET is not set. Invitation features will not work.');
}

/**
 * Hash a password using bcrypt
 * @param {string} password - Plain text password
 * @returns {Promise<string>} - Hashed password
 */
export const hashPassword = async (password) => {
  try {
    if (!password || typeof password !== 'string') {
      throw new Error('Password must be a non-empty string');
    }
    
    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }
    
    const salt = await bcrypt.genSalt(SALT_ROUNDS);
    const hashedPassword = await bcrypt.hash(password, salt);
    return hashedPassword;
  } catch (error) {
    console.error('Error hashing password:', error);
    throw new Error('Failed to hash password');
  }
};

/**
 * Verify a password against its hash
 * @param {string} password - Plain text password
 * @param {string} hashedPassword - Hashed password from database
 * @returns {Promise<boolean>} - True if password matches
 */
export const verifyPassword = async (password, hashedPassword) => {
  try {
    if (!password || !hashedPassword) {
      return false;
    }
    
    const isMatch = await bcrypt.compare(password, hashedPassword);
    return isMatch;
  } catch (error) {
    console.error('Error verifying password:', error);
    return false;
  }
};

/**
 * Generate a secure random invitation token
 * @returns {string} - Secure invitation token
 */
export const generateInvitationToken = () => {
  const timestamp = Date.now().toString();
  const randomBytes = CryptoJS.lib.WordArray.random(16).toString();
  const combined = `${timestamp}-${randomBytes}`;
  
  // Create a hash of the combined string for additional security
  const hash = CryptoJS.HmacSHA256(combined, INVITATION_TOKEN_SECRET).toString();
  
  return `inv_${hash.substring(0, 16)}`;
};

/**
 * Generate a secure random password for temporary use
 * @param {number} length - Password length (default: 12)
 * @returns {string} - Secure random password
 */
export const generateSecurePassword = (length = 12) => {
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  
  // Ensure at least one character from each category
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*';
  
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];
  
  // Fill the rest randomly
  for (let i = 4; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {object} - Validation result with score and feedback
 */
export const validatePasswordStrength = (password) => {
  if (!password) {
    return {
      isValid: false,
      score: 0,
      feedback: ['Password is required']
    };
  }
  
  const feedback = [];
  let score = 0;
  
  // Length check
  if (password.length < 6) {
    feedback.push('Password must be at least 6 characters long');
  } else if (password.length >= 8) {
    score += 1;
  }
  
  // Character variety checks
  if (!/[a-z]/.test(password)) {
    feedback.push('Password should contain lowercase letters');
  } else {
    score += 1;
  }
  
  if (!/[A-Z]/.test(password)) {
    feedback.push('Password should contain uppercase letters');
  } else {
    score += 1;
  }
  
  if (!/\d/.test(password)) {
    feedback.push('Password should contain numbers');
  } else {
    score += 1;
  }
  
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    feedback.push('Password should contain special characters');
  } else {
    score += 1;
  }
  
  // Common password patterns
  const commonPatterns = [
    /123456/,
    /password/i,
    /qwerty/i,
    /admin/i,
    /letmein/i
  ];
  
  if (commonPatterns.some(pattern => pattern.test(password))) {
    feedback.push('Password contains common patterns and is not secure');
    score = Math.max(0, score - 2);
  }
  
  const isValid = feedback.length === 0 && password.length >= 6;
  
  return {
    isValid,
    score,
    feedback,
    strength: score >= 4 ? 'Strong' : score >= 2 ? 'Medium' : 'Weak'
  };
};

/**
 * Encrypt sensitive data for storage
 * @param {string} data - Data to encrypt
 * @param {string} key - Encryption key
 * @returns {string} - Encrypted data
 */
export const encryptData = (data, key = INVITATION_TOKEN_SECRET) => {
  try {
    return CryptoJS.AES.encrypt(data, key).toString();
  } catch (error) {
    console.error('Error encrypting data:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt sensitive data from storage
 * @param {string} encryptedData - Encrypted data
 * @param {string} key - Encryption key
 * @returns {string} - Decrypted data
 */
export const decryptData = (encryptedData, key = INVITATION_TOKEN_SECRET) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, key);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Error decrypting data:', error);
    throw new Error('Failed to decrypt data');
  }
}; 